import React from "react";
import type {NodeProps} from "@xyflow/react";
import {Handle, Position} from "@xyflow/react";
import {alpha, Box, Typography, useTheme} from "@mui/material";
import type {DiffNode} from "~/prototypes/pages/taxonomy-canvas/approval/changeDiffGraph.ts";
import {DIFF_NODE_HEIGHT, DIFF_NODE_WIDTH} from "~/prototypes/pages/taxonomy-canvas/approval/changeDiffGraph.ts";
import {CHANGE_KIND_META} from "~/prototypes/pages/taxonomy-canvas/approval/changeSteps.ts";

/**
 * Category card in the change picture. Changed categories use the change's colour; removed ones are faded
 * with a dashed border; a renamed one shows the new name in bold and the old one struck through.
 */
export const ChangeDiffNodeView: React.FC<NodeProps<DiffNode>> = ({data}) => {
    const theme = useTheme();
    const color = theme.palette[CHANGE_KIND_META[data.kind].color].main;
    const isFocus = data.role === 'focus';
    const isMore = data.role === 'more';
    // Merge originals (dashed, as they were) and the sub-categories of a merge result (coloured)
    const isPrevious = data.role === 'previous';
    const isResult = data.role === 'result';
    // A previous category with a note is a merge original itself; without, it is one of its sub-categories
    const isPreviousMain = isPrevious && data.note !== undefined;
    const subline = data.note ?? (data.level === 0 ? 'Taxonomy' : `L${data.level}`);
    return <Box sx={{
        width: DIFF_NODE_WIDTH,
        height: DIFF_NODE_HEIGHT,
        px: 1.25,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        borderRadius: 1.5,
        border: isFocus || isPreviousMain ? 2 : 1,
        borderStyle: data.removed || isMore || isPrevious ? 'dashed' : 'solid',
        borderColor: isFocus || isPreviousMain || isResult ? color : 'mithraBlue.300',
        bgcolor: isFocus ? alpha(color, 0.16) : isResult ? alpha(color, 0.07) : 'background.paper',
        color: 'mithraBlue.800',
        opacity: data.removed ? 0.45 : isPrevious ? (isPreviousMain ? 0.8 : 0.6) : 1,
        boxShadow: isFocus && !data.removed ? 2 : 0,
    }}>
        <Handle type="target" position={Position.Left} isConnectable={false} style={{visibility: 'hidden'}}/>
        <Typography
            variant="body2"
            fontWeight={isFocus || isPreviousMain || data.prevLabel ? 700 : 500}
            fontStyle={isMore ? 'italic' : undefined}
            noWrap
            title={data.label}
            sx={{fontSize: 13}}
        >
            {data.label}
        </Typography>
        {data.prevLabel
            ? <Typography variant="caption" noWrap title={data.prevLabel}
                          sx={{fontSize: 11, lineHeight: 1.2, textDecoration: 'line-through', opacity: 0.75}}>
                {data.prevLabel}
            </Typography>
            : !isMore && <Typography variant="caption" noWrap title={subline} sx={{opacity: 0.75, fontSize: 11, lineHeight: 1.2}}>
                {subline}
            </Typography>}
        <Handle type="source" position={Position.Right} isConnectable={false} style={{visibility: 'hidden'}}/>
    </Box>
}
